import {
  chakra,
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Input,
  Switch,
  Textarea,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useCallback, useEffect, useState } from 'react';
import { EditorMode } from '@duke-hq/shared-constants/assessments';
import { unitAssessmentState } from '../../UnitAssessmentState';
import { useSnapshot } from 'valtio';
import { trpcAdmin } from '../../../lib/trpcAdmin';
import { GripVerticalIcon, Plus, TrashIcon } from 'lucide-react';
import { JSONContent } from '@tiptap/core';
import { DukeTiptapEditor } from '@duke-hq/ui/components/DukeTiptapEditor/DukeTiptapEditor';
import { Reorder, useDragControls } from 'framer-motion';
import { useDukeFileUpload } from '@duke-hq/app-admin-web/components/ReactFlow/nodes/blocknode/useDukeFileUpload';
import { SlideKind, UnitAssessmentMultiChoiceQuestionOption } from '@duke-hq/svc-api-db';
import { isDefined } from '@duke-hq/lib';
import { shortUuid7 } from '@duke-hq/id';

const List = chakra(Reorder.Group);
const ListItem = chakra(Reorder.Item);

type MultiChoiceQuestionOption = Pick<
  UnitAssessmentMultiChoiceQuestionOption,
  'answer' | 'feedback' | 'isCorrect' | 'position'
> & {
  id?: string;
  draftId: string;
};

type MultiChoiceQuestion = {
  options: MultiChoiceQuestionOption[];
  position?: number;
  questionGroupId?: string;
};

const initialFormData: MultiChoiceQuestion = {
  options: [],
};

const initialOption: MultiChoiceQuestionOption & { draftId: string } = {
  draftId: shortUuid7(),
  answer: '',
  feedback: '',
  isCorrect: false,
  position: 0,
};

const initialFormErrors = {
  options: false,
  answer: false,
  correctOption: false,
};

export function MultiChoiceQuestionEditor({ refetch }: { refetch: () => void }) {
  const unitAssessmentSnap = useSnapshot(unitAssessmentState);
  const [questionId, setQuestionId] = useState<string | null>(null);
  const [currentQuestionPosition, setCurrentQuestionPosition] = useState<number | undefined>(
    undefined,
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [formData, setFormData] = useState<MultiChoiceQuestion>(initialFormData);
  const [initialQuestionContent, setInitialQuestionContent] = useState<JSONContent | undefined>(
    undefined,
  );
  const [editorQuestionContent, setEditorQuestionContent] = useState<JSONContent | undefined>(
    undefined,
  );
  const [formErrors, setFormErrors] = useState({
    options: false,
    answer: false,
    correctOption: false,
  });
  const [deletedOptions, setDeletedOptions] = useState<string[]>([]);

  const { uploadFile } = useDukeFileUpload({
    fileAttributes: {
      attachedToKind: 'UNIT_ASSESSMENT',
      attachedToId: unitAssessmentSnap.currentAssessmentId,
    },
  });

  const utils = trpcAdmin.useUtils();

  useEffect(() => {
    resetForm();

    // Read through the proxy rather than the snapshot: the snapshot is deeply
    // readonly, and this seeds mutable form state from it.
    const slide = unitAssessmentState.currentSlide;
    const currentQuestion =
      slide?.kind === SlideKind.QUESTION_GROUP
        ? slide.questions[unitAssessmentSnap.currentMultiChoiceQuestionIndex]
        : undefined;

    if (unitAssessmentSnap.editorMode === EditorMode.EDIT && currentQuestion) {
      setQuestionId(currentQuestion.id);
      setCurrentQuestionPosition(currentQuestion.position);
      setFormData({
        options: currentQuestion.options.map((option) => ({
          id: option.id,
          draftId: option.id,
          questionId: option.questionId,
          answer: option.answer,
          feedback: option.feedback,
          isCorrect: option.isCorrect,
          position: option.position,
        })),
      });
      setInitialQuestionContent(currentQuestion.questionContent || { type: 'doc', content: [] });
    }
    if (unitAssessmentSnap.editorMode === EditorMode.CREATE) {
      setFormData(initialFormData);
      setInitialQuestionContent({ type: 'doc', content: [] });
    }

    return resetForm;
  }, [
    unitAssessmentSnap.currentSlide,
    unitAssessmentSnap.currentMultiChoiceQuestionIndex,
    unitAssessmentSnap.editorMode,
  ]);

  const resetForm = () => {
    setQuestionId(null);
    setCurrentQuestionPosition(undefined);
    setFormData(initialFormData);
    setFormErrors(initialFormErrors);
    setDeletedOptions([]);
    setHasUnsavedChanges(false);
  };

  const createMultiChoiceQuestion =
    trpcAdmin.unitAssessment.createMultiChoiceQuestion.useMutation();
  const updateMultiChoiceQuestion =
    trpcAdmin.unitAssessment.updateMultiChoiceQuestion.useMutation();
  const createMultiChoiceQuestionOption =
    trpcAdmin.unitAssessment.createMultiChoiceQuestionOption.useMutation();
  const updateMultiChoiceQuestionOption =
    trpcAdmin.unitAssessment.updateMultiChoiceQuestionOption.useMutation();
  const deleteMultiChoiceQuestionOptions =
    trpcAdmin.unitAssessment.deleteMultiChoiceQuestionOptions.useMutation();

  const draftVersionId = unitAssessmentSnap.draftVersionId;
  if (draftVersionId == null) {
    throw new Error('draftVersionId must not be null');
  }
  const getSlidesInput = {
    unitAssessmentVersionId: draftVersionId,
  };
  const reorderOptions = trpcAdmin.unitAssessment.reorderMultiChoiceQuestionOptions.useMutation({
    async onMutate(newOrder) {
      // Cancel outgoing fetches (so they don't overwrite our optimistic update)
      await utils.unitAssessment.getSlides.cancel();

      // Get the data from the queryCache
      const prevData = utils.unitAssessment.getSlides.getData();

      // Optimistically update the data with our new order
      utils.unitAssessment.getSlides.setData(getSlidesInput, (prevData) => {
        if (!prevData) {
          return;
        }

        // Create a new array with the same elements as the previous slides
        const prevSlideData = prevData.slides.find(
          (slide) => slide.id === unitAssessmentSnap.currentSlide?.id,
        );
        if (!prevSlideData || prevSlideData.kind !== SlideKind.QUESTION_GROUP) {
          return;
        }
        const updatedQuestionOptions = prevSlideData.questions.map((question) => {
          if (question.id === questionId) {
            return {
              ...question,
              // filter: find returns undefined for an id that is no longer present
              options: newOrder.optionIds
                .map((optionId) => question.options.find((option) => option.id === optionId))
                .filter(isDefined),
            };
          }
          return question;
        });

        return {
          ...prevData,
          slides: prevData.slides.map((slide) => {
            if (slide.id === prevSlideData.id) {
              return {
                ...prevSlideData,
                questions: updatedQuestionOptions,
              };
            }
            return slide;
          }),
        };
      });

      // Return the previous data so we can revert if something goes wrong
      return { prevData };
    },
    onError(_err, _newOrder, ctx) {
      // If the mutation fails, use the context-value from onMutate
      if (ctx) {
        utils.unitAssessment.getSlides.setData(getSlidesInput, ctx.prevData);
      }
    },
    onSuccess() {
      unitAssessmentState.setHasUnpublishedChanges(true);
    },
    onSettled() {
      void utils.unitAssessment.getSlides.invalidate();
    },
  });

  const onChangeEditor = (content: JSONContent) => {
    setEditorQuestionContent(content);
  };

  const addOption = () => {
    setFormData((prevData) => ({
      ...prevData,
      options: [
        ...prevData.options,
        {
          ...initialOption,
          draftId: shortUuid7(),
          position: prevData.options.length,
        },
      ],
    }));

    setHasUnsavedChanges(true);
    setFormErrors(initialFormErrors);
  };

  const deleteOption = (id: string) => {
    setFormData((prevData) => {
      const option = prevData.options.find((option) => option.draftId === id);
      if (option && option.id) {
        setDeletedOptions((prevDeletedOptions) => [...prevDeletedOptions, option.id!]);
      }
      return {
        ...prevData,
        options: prevData.options.filter((option) => option.draftId !== id),
      };
    });

    setHasUnsavedChanges(true);
  };

  const onFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, type } = e.target;
    const value = type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;

    setFormErrors(initialFormErrors);

    setFormData((prevData) => {
      const keys = name.split('.');
      if (keys.length === 1) {
        return { ...prevData, [name]: value };
      } else {
        // options is the only nested field on this form
        const [field, index, key] = keys;
        if (field !== 'options' || index === undefined) {
          return prevData;
        }
        const optionIndex = Number(index);
        return {
          ...prevData,
          options: prevData.options.map((option, idx) => {
            if (idx !== optionIndex) {
              return option;
            }
            switch (key) {
              case 'answer':
                return { ...option, answer: String(value) };
              case 'feedback':
                return { ...option, feedback: String(value) };
              case 'isCorrect':
                return { ...option, isCorrect: Boolean(value) };
              default:
                return option;
            }
          }),
        };
      }
    });

    setHasUnsavedChanges(true);
  };

  const validateForm = () => {
    const errors = {
      options: formData.options.length < 2,
      answer: formData.options.some((option) => option.answer === ''),
      correctOption: formData.options.filter((option) => option.isCorrect).length !== 1,
    };

    setFormErrors(errors);

    // Only submit if there are no errors
    return !Object.values(errors).some((error) => error);
  };

  const onSubmit = async () => {
    if (!validateForm() || !unitAssessmentSnap.currentSlide) {
      return;
    }

    // Delete the removed options
    if (deletedOptions.length > 0) {
      await deleteMultiChoiceQuestionOptions.mutateAsync(deletedOptions);
    }

    if (questionId) {
      // Update the question
      await updateMultiChoiceQuestion.mutateAsync({
        id: questionId,
        questionContent: editorQuestionContent ?? initialQuestionContent,
        position: currentQuestionPosition,
      });

      // Update the options
      for (let index = 0; index < formData.options.length; index++) {
        const option = formData.options[index];
        if (!option) {
          continue;
        }
        if (option.id) {
          await updateMultiChoiceQuestionOption.mutateAsync({
            id: option.id,
            answer: option.answer,
            feedback: option.feedback,
            isCorrect: option.isCorrect,
          });
        } else {
          await createMultiChoiceQuestionOption.mutateAsync({
            questionId: questionId,
            answer: option.answer,
            feedback: option.feedback,
            isCorrect: option.isCorrect,
            position: index,
          });
        }
      }
    } else {
      // Create the question and options if they don't exist yet
      const newQuestion = await createMultiChoiceQuestion.mutateAsync({
        questionGroupId: unitAssessmentSnap.currentSlide.id,
        questionContent: editorQuestionContent ?? initialQuestionContent,
        position:
          'questions' in unitAssessmentSnap.currentSlide
            ? unitAssessmentSnap.currentSlide.questions.length + 1
            : 1,
      });

      for (let index = 0; index < formData.options.length; index++) {
        const option = formData.options[index];
        if (!option) {
          continue;
        }
        await createMultiChoiceQuestionOption.mutateAsync({
          questionId: newQuestion.id,
          answer: option.answer,
          feedback: option.feedback,
          isCorrect: option.isCorrect,
          position: index,
        });
      }
    }

    refetch();
    setHasUnsavedChanges(false);
    unitAssessmentState.setHasUnpublishedChanges(true);
    unitAssessmentState.closeEditor();
  };

  const saveReorder = useCallback(() => {
    if (!questionId || hasUnsavedChanges) {
      return;
    }
    const optionIds = formData.options.map((option) => option.id!);
    if (!isDefined(optionIds)) {
      return;
    }
    reorderOptions.mutate({
      questionId: questionId,
      optionIds: optionIds,
    });
  }, [formData.options, hasUnsavedChanges, questionId, reorderOptions]);

  return (
    <form style={{ width: '100%' }}>
      <VStack spacing={8} align="stretch">
        <VStack spacing={2}>
          <Box w="full" h="full">
            <FormControl isRequired>
              <FormLabel fontSize="md">
                <Trans>Question</Trans>
              </FormLabel>
              <Box
                w="100%"
                borderWidth="1px"
                borderRadius="md"
                outline="1px"
                boxShadow="sm"
                minH="250px"
              >
                <DukeTiptapEditor
                  initialContent={initialQuestionContent}
                  onChange={onChangeEditor}
                  uploadFn={uploadFile}
                />
              </Box>
            </FormControl>
          </Box>
        </VStack>

        <List
          values={formData.options}
          onReorder={(newOrder) => {
            if (!hasUnsavedChanges) {
              setFormData((prevData) => ({
                ...prevData,
                options: newOrder as MultiChoiceQuestionOption[],
              }));
            }
          }}
          listStyleType="none"
        >
          {formData.options.map((option, index) => (
            <Item
              key={option.draftId}
              index={index}
              item={option}
              onFormChange={onFormChange}
              deleteOption={deleteOption}
              saveReorder={saveReorder}
              hasUnsavedChanges={hasUnsavedChanges}
            />
          ))}
        </List>

        <Flex
          direction="column"
          align="center"
          justify="center"
          h="80px"
          w="full"
          rounded="lg"
          border="3px dashed currentColor"
          color={'gray.200'}
        >
          <Button
            leftIcon={<Icon as={Plus} boxSize="24px" />}
            variant="tertiary"
            onClick={addOption}
          >
            <Trans>Add Option</Trans>
          </Button>
        </Flex>

        <FormControl p={2}>
          {formErrors.options ? (
            <FormHelperText textColor={'red.500'}>
              <Trans>Add at least two options</Trans>
            </FormHelperText>
          ) : null}
          {formErrors.answer ? (
            <FormHelperText textColor={'red.500'}>
              <Trans>Answer is required</Trans>
            </FormHelperText>
          ) : null}
          {formErrors.correctOption ? (
            <FormHelperText textColor={'red.500'}>
              <Trans>Select one option as correct</Trans>
            </FormHelperText>
          ) : null}
        </FormControl>

        <Button onClick={onSubmit} alignSelf={'flex-end'}>
          {unitAssessmentSnap.editorMode === EditorMode.CREATE ? (
            <Trans>Add Question</Trans>
          ) : (
            <Trans>Update Question</Trans>
          )}
        </Button>
      </VStack>
    </form>
  );
}

function Item({
  item,
  onFormChange,
  deleteOption,
  saveReorder,
  index,
  hasUnsavedChanges,
}: {
  item: MultiChoiceQuestionOption;
  onFormChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  deleteOption: (id: string) => void;
  saveReorder: () => void;
  index: number;
  hasUnsavedChanges: boolean;
}) {
  const dragControls = useDragControls();

  return (
    <ListItem
      key={item.draftId}
      value={item}
      position="relative"
      dragListener={false}
      dragControls={dragControls}
      onDragEnd={saveReorder}
    >
      <VStack spacing={2}>
        <FormControl>
          <HStack spacing={4} py={4} alignItems="center" justifyContent="space-between">
            <Flex alignItems="baseline" gap={4}>
              <FormLabel m={0} fontSize="md">
                <Trans>Option</Trans>&nbsp;{`${index + 1}`}
              </FormLabel>
              <Flex gap={2} alignItems="baseline">
                <FormLabel m={0}>
                  <Trans>Correct</Trans>
                </FormLabel>
                <Switch
                  name={`options.${index}.isCorrect`}
                  size="sm"
                  verticalAlign="bottom"
                  isChecked={item.isCorrect}
                  onChange={onFormChange}
                  colorScheme="green"
                />
              </Flex>
            </Flex>
            <HStack gap="4px" align="center">
              <Tooltip label={t`Remove option`}>
                <IconButton
                  aria-label={t`Remove option`}
                  variant="ghost"
                  size="xs"
                  icon={<Icon as={TrashIcon} color="fg.default" boxSize="14px" />}
                  onClick={() => deleteOption(item.draftId)}
                />
              </Tooltip>
              <IconButton
                _hover={{ color: hasUnsavedChanges ? 'fg.disabled' : 'brand.600' }}
                icon={<Icon as={GripVerticalIcon} />}
                cursor="grab"
                size="sm"
                variant="tertiary"
                aria-label={t`Reorder options`}
                onPointerDown={(e) => {
                  if (!hasUnsavedChanges) {
                    dragControls.start(e);
                  }
                }}
                isDisabled={hasUnsavedChanges}
              />
            </HStack>
          </HStack>
          <VStack spacing={2}>
            <Input
              autoComplete="off"
              placeholder="Answer"
              name={`options.${index}.answer`}
              value={item.answer}
              onChange={onFormChange}
            />
            <Textarea
              autoComplete="off"
              placeholder="Feedback"
              name={`options.${index}.feedback`}
              value={item.feedback}
              onChange={onFormChange}
            />
          </VStack>
        </FormControl>
      </VStack>
    </ListItem>
  );
}
